/*
  QBPN Free Quilting Tools
  All math runs in the shopper's browser. Inches throughout.

  Shared assumptions (shown to shoppers in each tool's notes):
  - 40" usable width of fabric (WOF) for standard quilting cotton.
  - 1/4" seam allowance.
  - Recommended purchase = calculated fabric + 3" per cut for squaring up,
    rounded UP to the next 1/8 yard (same rule as the QBPN Binding Calculator).
*/

(function (root) {

  var WOF = 40;
  var SEAM = 0.25;
  var EIGHTH_YARD = 4.5;
  var SQRT2 = Math.SQRT2;

  /* ---------- formatting ---------- */

  var FRACTIONS = { 0: "", 1: "⅛", 2: "¼", 3: "⅜", 4: "½", 5: "⅝", 6: "¾", 7: "⅞" };

  // Round UP to the next 1/8" (cutting sizes should never come out small).
  function up8(x) {
    return Math.ceil(x * 8 - 1e-9) / 8;
  }

  // Round to the nearest 1/8".
  function near8(x) {
    return Math.round(x * 8) / 8;
  }

  function isEighth(x) {
    return Math.abs(x * 8 - Math.round(x * 8)) < 1e-6;
  }

  // 3.875 -> 3⅞"
  function inch(x) {
    var eighths = Math.round(x * 8);
    var whole = Math.floor(eighths / 8);
    var rem = eighths % 8;
    if (whole === 0 && rem === 0) return '0"';
    if (whole === 0) return FRACTIONS[rem] + '"';
    return whole + FRACTIONS[rem] + '"';
  }

  function size(w, h) {
    return inch(w) + " × " + inch(h);
  }

  // Inches of fabric -> "1 ⅜ yards", rounded UP to the next 1/8 yard.
  function yards(inches) {
    var eighths = Math.max(1, Math.ceil(inches / EIGHTH_YARD - 1e-9));
    var whole = Math.floor(eighths / 8);
    var rem = eighths % 8;
    if (whole === 0) return FRACTIONS[rem] + " yard";
    if (rem === 0) return whole === 1 ? "1 yard" : whole + " yards";
    return whole + " " + FRACTIONS[rem] + " yards";
  }

  // Recommended purchase: add 3" per cut for squaring, round up to 1/8 yard.
  function purchase(inches, cuts) {
    return yards(inches + 3 * (cuts || 1));
  }

  function plural(n, one, many) {
    return n + " " + (n === 1 ? one : (many || one + "s"));
  }

  function num(x) {
    return typeof x === "number" && isFinite(x) && x > 0;
  }

  function err(message) {
    return { error: message };
  }

  /* ---------- shared fabric math ---------- */

  // Panels of `usable` width needed to span `dim`, losing 1/2" per seam.
  function panelsFor(dim, usable) {
    if (dim <= usable) return 1;
    return Math.ceil((dim - 2 * SEAM) / (usable - 2 * SEAM) - 1e-9);
  }

  // Strips needed when several cut lengths are pieced end to end from WOF strips.
  function piecedStrips(totalLength, usable) {
    return Math.ceil(totalLength / (usable - 2 * SEAM) - 1e-9);
  }

  /* ---------- 1. backing ---------- */

  function backing(v) {
    if (!num(v.width) || !num(v.length)) return err("Please enter your quilt width and length.");
    var extra = v.extra >= 0 ? v.extra : 4;
    var usable = num(v.usable) ? v.usable : WOF;
    var bw = v.width + 2 * extra;
    var bl = v.length + 2 * extra;

    // Option A: seams run top to bottom (panels side by side).
    var aPanels = panelsFor(bw, usable);
    var aTotal = aPanels * bl;
    // Option B: seams run side to side (panels stacked).
    var bPanels = panelsFor(bl, usable);
    var bTotal = bPanels * bw;

    var useA = aTotal <= bTotal;
    var panels = useA ? aPanels : bPanels;
    var panelLength = useA ? bl : bw;
    var total = panels * panelLength;

    var how;
    if (panels === 1) {
      how = "One piece, no seams";
    } else {
      how = plural(panels, "panel") + " cut " + inch(panelLength) + " long, seams running " +
        (useA ? "top to bottom" : "side to side");
    }

    var rows = [
      { label: "Backing size needed", value: size(bw, bl) },
      { label: "How to piece it", value: how },
      { label: "Calculated fabric required", value: inch(total) + " (" + yards(total) + ")" },
      { label: "Recommended fabric purchase", value: purchase(total, panels), highlight: true }
    ];

    var other = useA ? { p: bPanels, len: bw, t: bTotal, dir: "side to side" } : { p: aPanels, len: bl, t: aTotal, dir: "top to bottom" };
    var notes = [];
    if (other.p !== panels || other.t !== total) {
      notes.push("Prefer seams running " + other.dir + "? That takes " + plural(other.p, "panel") + " cut " +
        inch(other.len) + " long, " + yards(other.t) + " in total.");
    }
    notes.push("Backing size adds " + inch(extra) + " on every side. Longarm quilters often ask for 4\" to 6\" per side, so check with yours.");
    notes.push("Based on " + inch(usable) + " of usable fabric width and a ½\" seam where panels join. Recommended purchase adds 3\" per panel for squaring the cut ends and rounds up to the next ⅛ yard.");
    return { rows: rows, notes: notes };
  }

  /* ---------- 2. quilt size planner ---------- */

  var MATTRESSES = {
    crib: { name: "Crib", w: 28, l: 52 },
    twin: { name: "Twin", w: 38, l: 75 },
    twinxl: { name: "Twin XL", w: 38, l: 80 },
    full: { name: "Full / Double", w: 54, l: 75 },
    queen: { name: "Queen", w: 60, l: 80 },
    king: { name: "King", w: 76, l: 80 },
    calking: { name: "California King", w: 72, l: 84 }
  };

  function quiltSize(v) {
    var m = MATTRESSES[v.mattress];
    var mw = m ? m.w : v.mattressWidth;
    var ml = m ? m.l : v.mattressLength;
    if (!num(mw) || !num(ml)) return err("Please choose a bed size or enter your mattress width and length.");
    var side = v.sideDrop >= 0 ? v.sideDrop : 12;
    var foot = v.footDrop >= 0 ? v.footDrop : side;
    var tuck = v.pillowTuck >= 0 ? v.pillowTuck : 0;
    var shrink = v.shrink >= 0 ? v.shrink : 0;

    var fw = mw + 2 * side;
    var fl = ml + foot + tuck;
    var tw = Math.ceil(fw / (1 - shrink / 100) - 1e-9);
    var tl = Math.ceil(fl / (1 - shrink / 100) - 1e-9);

    var rows = [
      { label: "Mattress top", value: size(mw, ml) },
      { label: "Finished quilt size", value: size(fw, fl) },
      { label: "Make your quilt top about", value: size(tw, tl), highlight: true }
    ];
    var notes = [];
    if (num(v.depth) && side < v.depth) {
      notes.push("Your side drop (" + inch(side) + ") is less than your mattress depth (" + inch(v.depth) + "), so the quilt won't cover the sides of the mattress.");
    }
    notes.push("Width = mattress width + a drop on each side. Length = mattress length + foot drop + pillow tuck.");
    if (shrink > 0) {
      notes.push("The quilt top size allows " + shrink + "% for shrinkage from quilting and washing. Dense quilting and cotton batting shrink more.");
    }
    notes.push("Mattresses vary, especially pillow tops. If you can, measure the bed the quilt is for.");
    return { rows: rows, notes: notes };
  }

  function sizeChart(drop) {
    var out = [];
    Object.keys(MATTRESSES).forEach(function (k) {
      var m = MATTRESSES[k];
      out.push([m.name, size(m.w, m.l), size(m.w + 2 * drop, m.l + drop)]);
    });
    return out;
  }

  /* ---------- 3. HST / QST / flying geese ---------- */

  function triangles(v) {
    var unit = v.unit || "hst2";
    var f = v.finished;
    if (!num(f)) return err("Please enter the finished size of your unit.");
    var qty = num(v.qty) ? Math.ceil(v.qty) : 0;
    var trim = !!v.trim;
    var rows = [];
    var notes = [];
    var cut, per, sets;

    if (unit === "hst2" || unit === "hst4" || unit === "hst8" || unit === "qst") {
      if (unit === "hst2") {
        cut = trim ? f + 1 : f + 0.875;
        per = 2;
        notes.push("Draw a line corner to corner, sew ¼\" on both sides of the line, then cut on the line.");
        notes.push(trim ? "Uses finished size + 1\" so you can trim to an exact size." : "Uses finished size + ⅞\", the classic exact formula.");
      } else if (unit === "hst4") {
        cut = up8((f + 0.875) * SQRT2) + (trim ? 0.25 : 0);
        per = 4;
        notes.push("Sew ¼\" all the way around the outside of the two squares, then cut on both diagonals.");
        notes.push("Square size is (finished + ⅞\") × 1.414, rounded up to the next ⅛\". The outer edges are bias, so handle and press gently.");
      } else if (unit === "hst8") {
        cut = 2 * f + 1.75 + (trim ? 0.25 : 0);
        per = 8;
        notes.push("Draw both diagonals, sew ¼\" on both sides of each, then cut on both diagonals and through the middle both ways.");
        notes.push("Square size is (finished × 2) + 1¾\".");
      } else {
        cut = trim ? f + 1.5 : f + 1.25;
        per = 2;
        notes.push("Make two half-square triangles from the squares, place them right sides together with opposite colors facing, sew ¼\" on both sides of the diagonal and cut. Each pair of squares gives 2 hourglass units.");
        notes.push(trim ? "Uses finished size + 1½\" for trimming room." : "Uses finished size + 1¼\".");
      }
      rows.push({ label: "Cut squares", value: size(cut, cut), highlight: true });
      rows.push({ label: "Units from each pair of squares", value: String(per) });
      rows.push({ label: "Trim finished units to", value: size(f + 0.5, f + 0.5) });
      if (qty) {
        sets = Math.ceil(qty / per);
        rows.push({ label: "Squares to cut for " + qty + " units", value: plural(sets, "square") + " of each fabric" });
        if (sets * per > qty) notes.push("That makes " + sets * per + " units, " + (sets * per - qty) + " more than you need.");
      }
      return { rows: rows, notes: notes };
    }

    // Flying geese: finished width f, height f/2.
    var h = f / 2;
    rows.push({ label: "Finished goose size", value: size(f, h) });
    if (unit === "geese1") {
      rows.push({ label: "Goose rectangle (1 per unit)", value: size(f + 0.5, h + 0.5), highlight: true });
      rows.push({ label: "Sky squares (2 per unit)", value: size(h + 0.5, h + 0.5), highlight: true });
      if (qty) {
        rows.push({ label: "To make " + qty + " geese", value: plural(qty, "rectangle") + " and " + plural(qty * 2, "sky square") });
      }
      notes.push("Stitch-and-flip method: sew a sky square on the diagonal at each end of the rectangle and trim the corner.");
    } else {
      var big = f + 1.25 + (trim ? 0.25 : 0);
      var small = h + 0.875 + (trim ? 0.125 : 0);
      rows.push({ label: "Goose square (1 per 4 geese)", value: size(big, big), highlight: true });
      rows.push({ label: "Sky squares (4 per 4 geese)", value: size(small, small), highlight: true });
      if (qty) {
        sets = Math.ceil(qty / 4);
        rows.push({ label: "To make " + qty + " geese", value: plural(sets, "goose square") + " and " + plural(sets * 4, "sky square") });
        if (sets * 4 > qty) notes.push("That makes " + sets * 4 + " geese, " + (sets * 4 - qty) + " more than you need.");
      }
      notes.push("No-waste method: makes 4 identical geese from one large square and four small squares. Large = finished width + 1¼\", small = finished height + ⅞\".");
    }
    rows.push({ label: "Trim finished units to", value: size(f + 0.5, h + 0.5) });
    return { rows: rows, notes: notes };
  }

  /* ---------- 4. batting ---------- */

  var DEFAULT_BATTING_SIZES = "Crib: 45 x 60\nTwin: 72 x 90\nFull: 81 x 96\nQueen: 90 x 108\nKing: 120 x 120";

  function parseSizes(text) {
    var out = [];
    String(text || DEFAULT_BATTING_SIZES).split(/\n|<br\s*\/?>/i).forEach(function (line) {
      var m = line.replace(/<[^>]+>/g, "").match(/^\s*([^:]+):\s*([\d.]+)\s*[x×]\s*([\d.]+)/i);
      if (m) out.push({ name: m[1].trim(), w: parseFloat(m[2]), l: parseFloat(m[3]) });
    });
    return out;
  }

  function batting(v) {
    if (!num(v.width) || !num(v.length)) return err("Please enter your quilt width and length.");
    var o = v.overhang >= 0 ? v.overhang : 4;
    var bw = v.width + 2 * o;
    var bl = v.length + 2 * o;
    var short = Math.min(bw, bl);
    var long = Math.max(bw, bl);
    var rows = [{ label: "Batting size needed", value: size(bw, bl), highlight: true }];

    var fit = null;
    parseSizes(v.sizes).forEach(function (s) {
      var a = Math.min(s.w, s.l), b = Math.max(s.w, s.l);
      if (a >= short && b >= long && (!fit || a * b < fit.w * fit.l)) fit = s;
    });
    rows.push({
      label: "Smallest standard package that fits",
      value: fit ? fit.name + " (" + size(fit.w, fit.l) + ")" : "Larger than the listed packages. Use batting by the yard or join two pieces."
    });

    if (num(v.rollWidth)) {
      var r = v.rollWidth;
      var len;
      if (r >= short) {
        len = long;
        rows.push({ label: "From " + inch(r) + " wide batting by the yard", value: inch(len) + " long (" + yards(len) + ")" });
      } else {
        var pieces = panelsFor(short, r);
        len = pieces * long;
        rows.push({ label: "From " + inch(r) + " wide batting by the yard", value: plural(pieces, "piece") + " " + inch(long) + " long, butted and joined (" + yards(len) + ")" });
      }
    }

    var notes = [
      "Adds " + inch(o) + " of batting on every side of the quilt top. Many longarm quilters ask for 4\" or more per side, so check with yours.",
      "Package sizes vary by brand. Check the size on the label before you buy."
    ];
    return { rows: rows, notes: notes };
  }

  /* ---------- 5. precut converter ---------- */

  var PRECUTS = {
    minicharm: { name: "Mini charm pack (2½\" squares)", w: 2.5, h: 2.5, count: 42, kind: "square" },
    charm: { name: "Charm pack (5\" squares)", w: 5, h: 5, count: 42, kind: "square" },
    layercake: { name: "Layer cake (10\" squares)", w: 10, h: 10, count: 42, kind: "square" },
    jellyroll: { name: "Jelly roll (2½\" strips)", w: 2.5, h: WOF, count: 40, kind: "strip" },
    honeybun: { name: "Honey bun (1½\" strips)", w: 1.5, h: WOF, count: 40, kind: "strip" },
    fatquarter: { name: "Fat quarter (18\" × 21\")", w: 18, h: 21, count: 1, kind: "piece" },
    fateighth: { name: "Fat eighth (9\" × 21\")", w: 9, h: 21, count: 1, kind: "piece" }
  };

  // Inches of 40"-usable yardage needed to cut `n` pieces of a precut.
  function precutYardageInches(p, n) {
    if (p.kind === "strip") return n * p.w;
    if (p.kind === "piece") return n * (p.w === 18 ? 9 : 4.5);   // FQ = 1/4 yd, F8 = 1/8 yd
    var acrossWidth = Math.floor(WOF / p.w);
    var rowsPerYard = Math.floor(36 / p.h);
    var perYard = acrossWidth * rowsPerYard;
    return n / perYard * 36;
  }

  function fitIn(pw, ph, w, h) {
    var a = Math.floor(pw / w + 1e-9) * Math.floor(ph / h + 1e-9);
    var b = Math.floor(pw / h + 1e-9) * Math.floor(ph / w + 1e-9);
    return Math.max(a, b);
  }

  function precuts(v) {
    var mode = v.mode || "worth";
    var rows = [];
    var notes = [];
    var p;

    if (mode === "worth") {
      p = PRECUTS[v.precut];
      if (!p) return err("Please choose a precut.");
      var packs = num(v.packs) ? v.packs : 1;
      var count = num(v.count) ? Math.round(v.count) : p.count;
      var pieces = p.count === 1 ? packs : packs * count;
      var inches = precutYardageInches(p, pieces);
      rows.push({ label: "Pieces", value: plural(pieces, p.kind === "strip" ? "strip" : p.kind === "piece" ? "piece" : "square") });
      rows.push({ label: "Equal to about", value: yards(inches) + " of yardage", highlight: true });
      notes.push("This is how much 40\"-wide yardage it would take to cut the same pieces. Fat quarters count as ¼ yard and fat eighths as ⅛ yard.");
      if (p.count > 1) notes.push("Piece counts vary by manufacturer (often 40 or 42). Change the count to match your pack.");
      return { rows: rows, notes: notes };
    }

    if (mode === "fromYardage") {
      if (!num(v.yards)) return err("Please enter how many yards you have.");
      var len = v.yards * 36;
      ["minicharm", "charm", "layercake", "jellyroll", "honeybun", "fatquarter", "fateighth"].forEach(function (k) {
        var q = PRECUTS[k];
        var n;
        if (q.kind === "strip") n = Math.floor(len / q.w + 1e-9);
        else if (q.kind === "piece") n = Math.floor(len / (q.w === 18 ? 9 : 4.5) + 1e-9);
        else n = Math.floor(WOF / q.w) * Math.floor(len / q.h + 1e-9);
        rows.push({ label: q.name, value: String(n) });
      });
      notes.push("Assumes 40\" of usable width and straight cuts with no waste. Fat quarters and fat eighths assume the yardage is cut into quarter and eighth yards.");
      return { rows: rows, notes: notes };
    }

    // mode === "squares": how many precuts to cut N pieces of a size
    if (!num(v.pieceW) || !num(v.pieceQty)) return err("Please enter the piece size and how many you need.");
    var pw = v.pieceW;
    var ph = num(v.pieceH) ? v.pieceH : pw;
    var need = Math.ceil(v.pieceQty);
    ["charm", "layercake", "jellyroll", "honeybun", "fatquarter", "fateighth"].forEach(function (k) {
      var q = PRECUTS[k];
      var perPiece = fitIn(q.w, q.h, pw, ph);
      if (perPiece === 0) {
        rows.push({ label: q.name, value: "Too small" });
        return;
      }
      var perPack = perPiece * q.count;
      var packsNeeded = Math.ceil(need / perPack - 1e-9);
      var unit = q.count === 1 ? (k === "fatquarter" ? "fat quarter" : "fat eighth") : "pack";
      rows.push({ label: q.name, value: plural(packsNeeded, unit) + " (" + perPiece + " per " + (q.kind === "strip" ? "strip" : q.kind === "piece" ? "piece" : "square") + ")" });
    });
    notes.push("Cut size " + size(pw, ph) + ", " + need + " pieces. Assumes 42 squares per charm pack and layer cake, 40 strips per jelly roll and honey bun, fat quarters 18\" × 21\" and fat eighths 9\" × 21\".");
    notes.push("Counts the most pieces that fit in each precut with no waste allowance. If you want a variety of prints, you may need more.");
    return { rows: rows, notes: notes };
  }

  /* ---------- 6. borders ---------- */

  function borders(v) {
    if (!num(v.width) || !num(v.length)) return err("Please enter your quilt center width and length.");
    var widths = [v.b1, v.b2, v.b3].filter(num);
    if (!widths.length) return err("Please enter at least one border width.");
    var mitered = v.corners === "mitered";
    var usable = num(v.usable) ? v.usable : WOF;
    var w = v.width, l = v.length;
    var totalWidth = widths.reduce(function (a, b) { return a + b; }, 0);
    var table = { head: ["Border", "Cut strip width", "Cut 2 strips", "Cut 2 strips", "WOF strips", "Fabric"], rows: [] };
    var grandInches = 0, cuts = 0;

    widths.forEach(function (bw, i) {
      var cutW = bw + 2 * SEAM;
      var sideLen, topLen;
      if (mitered) {
        sideLen = v.length + 2 * totalWidth + 2;
        topLen = v.width + 2 * totalWidth + 2;
      } else {
        sideLen = l + 2 * SEAM;
        topLen = w + 2 * bw + 2 * SEAM;
      }
      var strips;
      if (Math.max(sideLen, topLen) > usable) {
        // Long borders: join WOF strips end to end.
        strips = piecedStrips(2 * sideLen + 2 * topLen, usable);
      } else {
        // Short borders: fit whole pieces into strips, longest first.
        var space = [];
        [sideLen, sideLen, topLen, topLen].sort(function (a, b) { return b - a; }).forEach(function (len) {
          for (var j = 0; j < space.length; j++) {
            if (space[j] >= len) { space[j] -= len; return; }
          }
          space.push(usable - len);
        });
        strips = space.length;
      }
      var inches = strips * cutW;
      grandInches += inches;
      cuts += 1;
      table.rows.push([
        "Border " + (i + 1) + " (" + inch(bw) + " finished)",
        inch(cutW),
        inch(sideLen) + " (sides)",
        inch(topLen) + " (top & bottom)",
        String(strips),
        yards(inches)
      ]);
      w += 2 * bw;
      l += 2 * bw;
    });

    var rows = [
      { label: "Finished quilt size with borders", value: size(w, l) },
      { label: "Border fabric if all borders are one fabric", value: purchase(grandInches, cuts), highlight: true }
    ];
    var notes = [];
    if (mitered) {
      notes.push("Mitered: sew the borders together into strip sets first, then attach and miter. Each strip includes 2× the total border width plus 2\" extra for the miter.");
    } else {
      notes.push("Straight (butted) corners: sew the side borders first, then the top and bottom. Measure through the center of your quilt and cut to that length, since tops are rarely exact.");
    }
    notes.push("WOF strips are joined end to end where a border is longer than the " + inch(usable) + " usable width. To avoid seams in a border, cut it along the length of the fabric instead; you'll need at least the longest strip length in yardage.");
    notes.push("Recommended purchase adds 3\" per border fabric and rounds up to the next ⅛ yard. If each border is a different fabric, buy the amount shown for each one.");
    return { rows: rows, table: table, notes: notes };
  }

  /* ---------- 7. quilt layout ---------- */

  function layout(v) {
    var b = v.block;
    if (!num(b)) return err("Please enter your finished block size.");
    var s = v.sashing > 0 ? v.sashing : 0;
    var frame = !!v.frame && s > 0;
    var stones = !!v.cornerstones && s > 0;
    var c, r;

    function span(n) { return n * b + (n - 1) * s + (frame ? 2 * s : 0); }

    if (v.mode === "target") {
      if (!num(v.targetW) || !num(v.targetL)) return err("Please enter the quilt size you want.");
      var extra = frame ? s : -s;
      c = Math.max(1, Math.round((v.targetW - extra) / (b + s)));
      r = Math.max(1, Math.round((v.targetL - extra) / (b + s)));
      // Round could overshoot by more than the closest option; check neighbours.
      [c - 1, c + 1].forEach(function (n) { if (n >= 1 && Math.abs(span(n) - v.targetW) < Math.abs(span(c) - v.targetW)) c = n; });
      [r - 1, r + 1].forEach(function (n) { if (n >= 1 && Math.abs(span(n) - v.targetL) < Math.abs(span(r) - v.targetL)) r = n; });
    } else {
      c = Math.round(v.across);
      r = Math.round(v.down);
      if (!(c >= 1) || !(r >= 1)) return err("Please enter how many blocks across and down.");
    }

    var cw = span(c), cl = span(r);
    var rows = [
      { label: "Layout", value: c + " blocks across × " + r + " down" },
      { label: "Blocks needed", value: String(c * r), highlight: true },
      { label: "Cut / unfinished block size", value: size(b + 0.5, b + 0.5) },
      { label: "Quilt size before borders", value: size(cw, cl), highlight: true }
    ];
    var notes = [];
    if (v.mode === "target") notes.push("Closest layout to " + size(v.targetW, v.targetL) + ". Add borders to make up the difference.");

    if (s > 0) {
      var cutS = s + 0.5;
      var vert = r * (c - 1) + (frame ? 2 * r : 0);
      var shortCount, longStrips = 0, longLen = 0, stoneCount = 0;
      if (stones) {
        shortCount = vert + (r - 1) * c + (frame ? 2 * c : 0);
        stoneCount = frame ? (r + 1) * (c + 1) : (r - 1) * (c - 1);
      } else {
        shortCount = vert;
        longStrips = (r - 1) + (frame ? 2 : 0);
        longLen = cw + 0.5;
      }
      rows.push({ label: "Short sashing strips", value: shortCount + " cut " + size(cutS, b + 0.5) });
      if (stones && stoneCount) rows.push({ label: "Cornerstones", value: stoneCount + " cut " + size(cutS, cutS) });
      if (longStrips) rows.push({ label: "Long sashing strips", value: longStrips + " cut " + size(cutS, longLen) });

      var perStrip = Math.floor(WOF / (b + 0.5));
      var wofStrips = Math.ceil(shortCount / perStrip - 1e-9);
      if (longStrips) wofStrips += piecedStrips(longStrips * longLen, WOF);
      var sashInches = wofStrips * cutS;
      var stoneInches = 0;
      if (stoneCount) stoneInches = Math.ceil(stoneCount / Math.floor(WOF / cutS) - 1e-9) * cutS;
      rows.push({ label: "Sashing fabric (" + plural(wofStrips, "WOF strip") + " " + inch(cutS) + " wide)", value: purchase(sashInches, 1) });
      if (stoneInches) rows.push({ label: "Cornerstone fabric", value: purchase(stoneInches, 1) });
      notes.push("Long sashing strips are cut from WOF strips joined end to end. Measure your rows before cutting them.");
    }
    notes.push("Based on 40\" usable fabric width and ¼\" seams. Recommended purchase adds 3\" and rounds up to the next ⅛ yard.");
    return { rows: rows, notes: notes };
  }

  /* ---------- 8. setting triangles (on point) ---------- */

  function onPoint(v) {
    var b = v.block;
    if (!num(b)) return err("Please enter your finished block size.");
    var a = Math.round(v.across), d = Math.round(v.down);
    if (!(a >= 1) || !(d >= 1)) return err("Please enter how many blocks across and down.");
    var extra = v.oversize ? 1 : 0;
    var diag = b * SQRT2;
    var sideSq = up8(diag + 1.25) + extra;
    var cornerSq = up8(b / SQRT2 + 0.875) + extra;
    var sides = 2 * (a - 1) + 2 * (d - 1);
    var sideSquares = Math.ceil(sides / 4);

    var rows = [
      { label: "Blocks needed", value: String(a * d + (a - 1) * (d - 1)) + " (" + a * d + " full rows + " + (a - 1) * (d - 1) + " in between)" },
      { label: "Quilt size before borders", value: size(near8(a * diag), near8(d * diag)), highlight: true },
      { label: "Side setting triangles", value: sides ? sides + " (cut " + plural(sideSquares, "square") + " " + inch(sideSq) + ", then cut twice on the diagonal)" : "None" },
      { label: "Corner setting triangles", value: "4 (cut 2 squares " + inch(cornerSq) + ", then cut once on the diagonal)" },
      { label: "Side triangle square", value: size(sideSq, sideSq), highlight: true },
      { label: "Corner triangle square", value: size(cornerSq, cornerSq), highlight: true }
    ];
    var notes = [
      "Side squares = finished block × 1.414 + 1¼\". Corner squares = finished block ÷ 1.414 + ⅞\". Both rounded up to the next ⅛\".",
      "Cutting this way keeps the straight grain on the outside edges of the quilt so they don't stretch."
    ];
    if (sides % 4) notes.push("You'll have " + (sideSquares * 4 - sides) + " spare side triangles.");
    if (extra) notes.push("Oversized by 1\" so the blocks float slightly and you can trim the edges straight.");
    notes.push("Each block measures " + inch(near8(diag)) + " corner to corner when set on point.");
    return { rows: rows, notes: notes };
  }

  /* ---------- 9. fabric yardage from pieces and strips ---------- */

  function yardage(v) {
    var mode = v.mode || "pieces";
    var usable = num(v.usable) ? v.usable : WOF;
    var rows = [], notes = [];

    if (mode === "pieces") {
      if (!num(v.pieceW) || !num(v.pieceQty)) return err("Please enter the cut size and how many pieces you need.");
      var w = v.pieceW, h = num(v.pieceH) ? v.pieceH : v.pieceW;
      var qty = Math.ceil(v.pieceQty);
      var best = null;
      [[h, w], [w, h]].forEach(function (o) {
        var stripW = o[0], pieceLen = o[1];
        var per = Math.floor(usable / pieceLen + 1e-9);
        if (per < 1) return;
        var strips = Math.ceil(qty / per - 1e-9);
        var inches = strips * stripW;
        if (!best || inches < best.inches) best = { stripW: stripW, per: per, strips: strips, inches: inches };
      });
      if (!best) return err("That piece is wider than the fabric. Try the wide fabric width.");
      rows.push({ label: "Cut strips", value: plural(best.strips, "strip") + " " + inch(best.stripW) + " × WOF" });
      rows.push({ label: "Pieces per strip", value: String(best.per) });
      rows.push({ label: "Calculated fabric required", value: inch(best.inches) + " (" + yards(best.inches) + ")" });
      rows.push({ label: "Recommended fabric purchase", value: purchase(best.inches, 1), highlight: true });
      if (best.strips * best.per > qty) notes.push("You'll have " + (best.strips * best.per - qty) + " extra pieces.");
    } else if (mode === "strips") {
      if (!num(v.stripW) || !num(v.stripQty)) return err("Please enter the strip width and how many strips.");
      var total = Math.ceil(v.stripQty) * v.stripW;
      rows.push({ label: "Calculated fabric required", value: inch(total) + " (" + yards(total) + ")" });
      rows.push({ label: "Recommended fabric purchase", value: purchase(total, 1), highlight: true });
    } else if (mode === "reverse") {
      if (!num(v.yards) || !num(v.pieceW)) return err("Please enter your yardage and the cut size.");
      var pw = v.pieceW, ph = num(v.pieceH) ? v.pieceH : v.pieceW;
      var len = v.yards * 36;
      var n = Math.max(
        Math.floor(len / ph + 1e-9) * Math.floor(usable / pw + 1e-9),
        Math.floor(len / pw + 1e-9) * Math.floor(usable / ph + 1e-9)
      );
      rows.push({ label: "Pieces you can cut", value: String(n), highlight: true });
      notes.push("Assumes " + inch(usable) + " of usable width and straight, perfect cuts with no shrinkage. Leave a little room for squaring up.");
      return { rows: rows, notes: notes };
    } else {
      // convert: pattern yardage at one width to another width
      if (!num(v.yards) || !num(v.fromWidth) || !num(v.toWidth)) return err("Please enter the yardage and both fabric widths.");
      var conv = v.yards * 36 * v.fromWidth / v.toWidth;
      rows.push({ label: "Approximate yardage at " + inch(v.toWidth) + " wide", value: yards(conv), highlight: true });
      notes.push("A straight area conversion. It works best for backing and large pieces; for patterns with many small cuts, use the pieces calculator above.");
      return { rows: rows, notes: notes };
    }
    notes.push("Based on " + inch(usable) + " of usable fabric width. Recommended purchase adds 3\" and rounds up to the next ⅛ yard.");
    return { rows: rows, notes: notes };
  }

  /* ---------- 10. block resizer ---------- */

  var ALLOWANCE = { square: 0.5, rect: 0.5, hst: 0.875, qst: 1.25 };
  var SHAPE_NAMES = { square: "Square", rect: "Rectangle", hst: "Square for HSTs", qst: "Square for QSTs" };

  function resize(v) {
    if (!num(v.from) || !num(v.to)) return err("Please enter the original and new finished block sizes.");
    var k = v.to / v.from;
    var pieces = (v.pieces || []).filter(function (p) { return num(p.w); });
    var rows = [
      { label: "Scale factor", value: (Math.round(k * 1000) / 1000) + "×" },
      { label: "Printer / copier setting for templates", value: Math.round(k * 1000) / 10 + "%", highlight: true }
    ];
    var table = null;
    var notes = [];
    var offGrid = false;

    if (pieces.length) {
      table = { head: ["Piece", "Shape", "Pattern cut size", "New cut size"], rows: [] };
      pieces.forEach(function (p, i) {
        var a = ALLOWANCE[p.shape] || 0.5;
        var h = p.shape === "rect" && num(p.h) ? p.h : p.w;
        var nw = (p.w - a) * k + a;
        var nh = (h - a) * k + a;
        var exact = isEighth(nw) && isEighth(nh);
        if (!exact) offGrid = true;
        table.rows.push([
          p.name || "Piece " + (i + 1),
          SHAPE_NAMES[p.shape] || "Square",
          size(p.w, h),
          size(up8(nw), up8(nh)) + (exact ? "" : " *")
        ]);
      });
    }
    notes.push("Each cut size is scaled on its finished size: the seam allowance (½\" for squares and rectangles, ⅞\" for HST squares, 1¼\" for QST squares) is taken off, the rest is scaled, and the allowance is added back.");
    if (offGrid) notes.push("* Sizes marked with a star don't land on a ⅛\" ruler line, so they're rounded up. Cut at that size and trim units to the exact size after sewing.");
    notes.push("The copier percentage is for paper templates and foundation patterns. Scale the finished lines, then add seam allowances if your templates don't include them.");
    notes.push("Changing block size changes yardage too. Use the Fabric Yardage Calculator with your new cut sizes.");
    return { rows: rows, table: table, notes: notes };
  }

  var TOOLS = {
    backing: backing,
    quiltSize: quiltSize,
    triangles: triangles,
    batting: batting,
    precuts: precuts,
    borders: borders,
    layout: layout,
    onPoint: onPoint,
    yardage: yardage,
    resize: resize
  };

  var api = {
    tools: TOOLS,
    helpers: { inch: inch, yards: yards, up8: up8, purchase: purchase, panelsFor: panelsFor, sizeChart: sizeChart, parseSizes: parseSizes, MATTRESSES: MATTRESSES }
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
    return;
  }

  /* =================== Browser UI =================== */

  function readForm(rootEl) {
    var v = {};
    rootEl.querySelectorAll("[data-field]").forEach(function (el) {
      var name = el.getAttribute("data-field");
      if (el.closest("[data-piece-row]")) return;
      if (el.closest("[hidden]")) return;
      if (el.type === "checkbox") v[name] = el.checked;
      else if (el.tagName === "SELECT" || el.type === "radio") {
        if (el.type === "radio" && !el.checked) return;
        var n = parseFloat(el.value);
        v[name] = !isNaN(n) && String(n) === el.value ? n : el.value;
      } else if (el.tagName === "TEXTAREA" || el.type === "hidden") {
        v[name] = el.value;
      } else {
        v[name] = el.value === "" ? undefined : parseFloat(el.value);
      }
    });
    var pieceRows = rootEl.querySelectorAll("[data-piece-row]");
    if (pieceRows.length) {
      v.pieces = [];
      pieceRows.forEach(function (row) {
        var get = function (f) { var el = row.querySelector('[data-field="' + f + '"]'); return el ? el.value : ""; };
        v.pieces.push({ name: get("name"), shape: get("shape"), w: parseFloat(get("w")), h: parseFloat(get("h")) });
      });
    }
    return v;
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function render(rootEl, out) {
    var results = rootEl.querySelector("[data-results]");
    var error = rootEl.querySelector("[data-error]");
    if (out.error) {
      results.hidden = true;
      error.textContent = out.error;
      error.hidden = false;
      return;
    }
    error.hidden = true;
    var body = results.querySelector("[data-results-body]");
    body.innerHTML = "";
    (out.rows || []).forEach(function (r) {
      var row = el("div", "qbpn-result" + (r.highlight ? " qbpn-result--highlight" : ""));
      row.appendChild(el("span", null, r.label));
      row.appendChild(el("strong", null, r.value));
      body.appendChild(row);
    });
    if (out.table) {
      var wrap = el("div", "qbpn-tool-table-wrap");
      var t = el("table", "qbpn-tool-table");
      var thead = el("thead"), tr = el("tr");
      out.table.head.forEach(function (h) { tr.appendChild(el("th", null, h)); });
      thead.appendChild(tr);
      t.appendChild(thead);
      var tb = el("tbody");
      out.table.rows.forEach(function (r) {
        var row = el("tr");
        r.forEach(function (c, i) {
          var td = el("td", null, c);
          td.setAttribute("data-label", out.table.head[i]);
          row.appendChild(td);
        });
        tb.appendChild(row);
      });
      t.appendChild(tb);
      wrap.appendChild(t);
      body.appendChild(wrap);
    }
    (out.notes || []).forEach(function (n) {
      body.appendChild(el("p", "qbpn-binding-note", n));
    });
    results.hidden = false;
  }

  function syncModes(rootEl) {
    var modeEl = rootEl.querySelector('[data-field="mode"]:checked, select[data-field="mode"]');
    var mode = modeEl ? modeEl.value : null;
    rootEl.querySelectorAll("[data-show-mode]").forEach(function (g) {
      g.hidden = mode !== null && g.getAttribute("data-show-mode").split(" ").indexOf(mode) === -1;
    });
    var unitEl = rootEl.querySelector('[data-field="unit"]');
    if (unitEl) {
      rootEl.querySelectorAll("[data-show-unit]").forEach(function (g) {
        g.hidden = g.getAttribute("data-show-unit").split(" ").indexOf(unitEl.value) === -1;
      });
    }
    var mat = rootEl.querySelector('[data-field="mattress"]');
    if (mat) {
      rootEl.querySelectorAll("[data-show-custom]").forEach(function (g) { g.hidden = mat.value !== "custom"; });
    }
  }

  function pieceRow(tpl) {
    var row = tpl.content.firstElementChild.cloneNode(true);
    row.querySelector("[data-remove-piece]").addEventListener("click", function () { row.remove(); });
    var shape = row.querySelector('[data-field="shape"]');
    var hWrap = row.querySelector("[data-rect-only]");
    var upd = function () { hWrap.hidden = shape.value !== "rect"; };
    shape.addEventListener("change", upd);
    upd();
    return row;
  }

  function init(rootEl) {
    if (rootEl.__qbpnInit) return;
    rootEl.__qbpnInit = true;
    var fn = TOOLS[rootEl.getAttribute("data-qbpn-tool")];
    if (!fn) return;

    var tpl = rootEl.querySelector("template[data-piece-template]");
    var list = rootEl.querySelector("[data-piece-list]");
    if (tpl && list) {
      for (var i = 0; i < 3; i++) list.appendChild(pieceRow(tpl));
      rootEl.querySelector("[data-add-piece]").addEventListener("click", function () {
        list.appendChild(pieceRow(tpl));
      });
    }

    var chart = rootEl.querySelector("[data-size-chart]");
    if (chart) {
      var drop = parseFloat(chart.getAttribute("data-drop")) || 12;
      sizeChart(drop).forEach(function (r) {
        var tr = el("tr");
        r.forEach(function (c) { tr.appendChild(el("td", null, c)); });
        chart.appendChild(tr);
      });
    }

    var calc = function () { render(rootEl, fn(readForm(rootEl))); };
    rootEl.querySelector("[data-calculate]").addEventListener("click", calc);
    rootEl.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); calc(); }
    });
    rootEl.addEventListener("change", function () { syncModes(rootEl); });
    syncModes(rootEl);
  }

  function boot() {
    document.querySelectorAll("[data-qbpn-tool]").forEach(init);
  }

  root.QBPNQuiltTools = api;
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);

})(typeof window !== "undefined" ? window : this);
